import { buildTableFieldPickerOption, type TableFieldPickerOption } from "./tableMappingBuilder";

export type TableResultSourceKind = "aggregation" | "hits";

export type TableResultSource = {
  id: string;
  kind: TableResultSourceKind;
  label: string;
  rowSource?: string;
  aggPath?: string;
};

export type TableResultFieldOption = TableFieldPickerOption & {
  type: string;
};

export type OpenSearchTableAnalysis = {
  sources: TableResultSource[];
  fieldsBySource: Record<string, TableResultFieldOption[]>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function aggGroupName(aggPath: string): string {
  const parts = aggPath.split(".").filter(Boolean);
  let name = parts[parts.length - 1] ?? "aggregation";
  if (name === "aggregations" && parts.length > 1) {
    name = parts[parts.length - 2];
  }
  if (name.startsWith("by_")) {
    name = name.slice(3);
  }
  return name || "aggregation";
}

function fieldOption(path: string, type: string, group?: string): TableResultFieldOption {
  return { ...buildTableFieldPickerOption({ path, group, type }), type };
}

const BUCKET_META_KEYS = new Set([
  "buckets",
  "doc_count_error_upper_bound",
  "sum_other_doc_count",
  "meta",
]);

function isTermsBucketAgg(value: unknown): value is Record<string, unknown> & { buckets: unknown[] } {
  return isRecord(value) && Array.isArray(value.buckets);
}

function addTermsBucketFieldOptions(
  fieldMap: Map<string, TableResultFieldOption>,
  group: string
) {
  fieldMap.set(`${group}:key`, fieldOption("key", "string", group));
  fieldMap.set(`${group}:doc_count`, fieldOption("doc_count", "number", group));
}

function findBucketsUnder(node: unknown): unknown[] | null {
  if (node == null) return null;
  if (Array.isArray(node)) {
    return node.length > 0 ? node : null;
  }
  if (!isRecord(node)) return null;
  if (isTermsBucketAgg(node)) {
    return node.buckets;
  }
  for (const value of Object.values(node)) {
    const found = findBucketsUnder(value);
    if (found) return found;
  }
  return null;
}

function collectAggMetricPaths(bucket: Record<string, unknown>, rootGroup: string): TableResultFieldOption[] {
  const options: TableResultFieldOption[] = [
    fieldOption("key", "string", rootGroup),
    fieldOption("doc_count", "number", rootGroup),
  ];
  const seen = new Set(options.map((o) => `${o.group}:${o.path}`));

  for (const [name, value] of Object.entries(bucket)) {
    if (name === "key" || name === "doc_count" || name === "key_as_string") continue;
    if (!isRecord(value)) continue;
    if (isTermsBucketAgg(value)) {
      const subOpts = [fieldOption("key", "string", name), fieldOption("doc_count", "number", name)];
      for (const opt of subOpts) {
        const id = `${opt.group}:${opt.path}`;
        if (!seen.has(id)) {
          seen.add(id);
          options.push(opt);
        }
      }
      continue;
    }
    if (!isMetricAggResult(value)) continue;
    const id = `${rootGroup}:${name}`;
    if (!seen.has(id)) {
      seen.add(id);
      options.push(fieldOption(name, metricType(extractMetricSample(value)), rootGroup));
    }
  }
  return options;
}

/** Her kök aggregation (name, category, …) için key/doc_count/metric seçenekleri. */
function extractSiblingAggregationFieldOptions(aggregations: Record<string, unknown>): TableResultFieldOption[] {
  const fieldMap = new Map<string, TableResultFieldOption>();

  for (const [aggName, aggValue] of Object.entries(aggregations)) {
    if (!isRecord(aggValue)) continue;
    mergeAggNodeStructure(fieldMap, aggValue, aggName);
  }

  return Array.from(fieldMap.values());
}

function mergeAggNodeStructure(
  fieldMap: Map<string, TableResultFieldOption>,
  aggNode: Record<string, unknown>,
  rootGroup: string
) {
  if (isTermsBucketAgg(aggNode)) {
    addTermsBucketFieldOptions(fieldMap, rootGroup);
    for (const bucket of aggNode.buckets) {
      if (isRecord(bucket)) {
        mergeBucketFields(fieldMap, bucket, rootGroup);
      }
    }
    return;
  }

  const buckets = findBucketsUnder(aggNode);
  if (buckets) {
    addTermsBucketFieldOptions(fieldMap, rootGroup);
    for (const bucket of buckets) {
      if (!isRecord(bucket)) continue;
      for (const opt of collectAggMetricPaths(bucket, rootGroup)) {
        fieldMap.set(`${opt.group}:${opt.path}`, opt);
      }
    }
    return;
  }

  if (isStandaloneMetricAgg(aggNode)) {
    addStandaloneMetricFieldOptions(fieldMap, aggNode, rootGroup);
    return;
  }

  if (typeof aggNode.doc_count === "number") {
    fieldMap.set(`${rootGroup}:doc_count`, fieldOption("doc_count", "number", rootGroup));
    fieldMap.set(`${rootGroup}:key`, fieldOption("key", "string", rootGroup));
    return;
  }

  for (const [name, value] of Object.entries(aggNode)) {
    if (BUCKET_META_KEYS.has(name)) continue;
    if (isTermsBucketAgg(value)) {
      addTermsBucketFieldOptions(fieldMap, name);
    }
  }
}

function findFirstBuckets(
  node: unknown,
  aggPath = ""
): { buckets: unknown[]; aggPath: string; aggNode: Record<string, unknown> } | null {
  if (node == null) {
    return null;
  }
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findFirstBuckets(item, aggPath);
      if (found) {
        return found;
      }
    }
    return null;
  }
  if (!isRecord(node)) {
    return null;
  }
  const buckets = node.buckets;
  if (Array.isArray(buckets) && buckets.length > 0) {
    return { buckets, aggPath: aggPath || "aggregations", aggNode: node };
  }
  for (const [key, value] of Object.entries(node)) {
    if (key === "buckets") {
      continue;
    }
    const nextPath = aggPath ? `${aggPath}.${key}` : key;
    const found = findFirstBuckets(value, nextPath);
    if (found) {
      return found;
    }
  }
  return null;
}

function metricType(value: unknown): string {
  if (typeof value === "number") {
    return "number";
  }
  if (typeof value === "boolean") {
    return "boolean";
  }
  if (typeof value === "string") {
    return "string";
  }
  return "unknown";
}

function extractMetricSample(value: Record<string, unknown>): unknown {
  if ("value" in value) {
    return value.value;
  }
  for (const key of ["avg", "sum", "min", "max", "count"]) {
    if (key in value) {
      return value[key];
    }
  }
  return undefined;
}

function isMetricAggResult(value: Record<string, unknown>): boolean {
  if (Array.isArray(value.buckets)) {
    return false;
  }
  if ("value" in value) {
    return true;
  }
  if (Object.keys(value).length === 0) {
    return true;
  }
  return "count" in value || "avg" in value || "sum" in value || "min" in value || "max" in value;
}

function isStandaloneMetricAgg(value: Record<string, unknown>): boolean {
  if (Array.isArray(value.buckets) || findBucketsUnder(value)) {
    return false;
  }
  return isMetricAggResult(value);
}

function addStandaloneMetricFieldOptions(
  fieldMap: Map<string, TableResultFieldOption>,
  aggNode: Record<string, unknown>,
  rootGroup: string
) {
  if ("value" in aggNode) {
    fieldMap.set(`${rootGroup}:value`, fieldOption("value", metricType(aggNode.value), rootGroup));
  }
  for (const key of ["avg", "sum", "min", "max", "count"]) {
    if (!(key in aggNode)) continue;
    fieldMap.set(`${rootGroup}:${key}`, fieldOption(key, metricType(aggNode[key]), rootGroup));
  }
  if (typeof aggNode.doc_count === "number") {
    fieldMap.set(`${rootGroup}:doc_count`, fieldOption("doc_count", "number", rootGroup));
  }
}

function mergeBucketFields(
  fieldMap: Map<string, TableResultFieldOption>,
  bucket: Record<string, unknown>,
  rootGroup: string
) {
  addTermsBucketFieldOptions(fieldMap, rootGroup);

  for (const [name, value] of Object.entries(bucket)) {
    if (name === "key" || name === "doc_count" || name === "key_as_string") {
      continue;
    }
    if (!isRecord(value)) {
      continue;
    }
    if (isTermsBucketAgg(value)) {
      addTermsBucketFieldOptions(fieldMap, name);
      continue;
    }
    if (!isMetricAggResult(value)) {
      continue;
    }
    const sample = extractMetricSample(value);
    const existing = fieldMap.get(`${rootGroup}:${name}`);
    if (existing && sample == null) {
      continue;
    }
    fieldMap.set(`${rootGroup}:${name}`, fieldOption(name, metricType(sample), rootGroup));
  }
}

function extractAggBucketFields(
  buckets: unknown[],
  aggPath: string,
  aggNode?: Record<string, unknown>
): TableResultFieldOption[] {
  const rootGroup = aggGroupName(aggPath);
  const fieldMap = new Map<string, TableResultFieldOption>();

  if (aggNode) {
    mergeAggNodeStructure(fieldMap, aggNode, rootGroup);
  }

  for (const bucket of buckets) {
    if (!isRecord(bucket)) {
      continue;
    }
    mergeBucketFields(fieldMap, bucket, rootGroup);
  }

  if (fieldMap.size === 0) {
    addTermsBucketFieldOptions(fieldMap, rootGroup);
  }

  return Array.from(fieldMap.values());
}

function hitsFieldOption(field: string, subPath: string, type: string): TableResultFieldOption {
  const docField = field.trim();
  const path = subPath.trim();
  const label = path ? `${docField}.${path}` : docField;
  return { path, field: docField, label, type };
}

function addHitFieldOption(
  fieldMap: Map<string, TableResultFieldOption>,
  field: string,
  subPath: string,
  type: string
) {
  const key = `${field}:${subPath}`;
  const existing = fieldMap.get(key);
  if (existing && existing.type !== "unknown") {
    return;
  }
  fieldMap.set(key, hitsFieldOption(field, subPath, type));
}

function extractDocumentFields(
  node: unknown,
  parentField = "",
  parentSubPath = "",
  depth = 0
): TableResultFieldOption[] {
  if (node == null || depth > 6) {
    return [];
  }
  if (Array.isArray(node)) {
    if (node.length === 0) {
      return [];
    }
    const fieldMap = new Map<string, TableResultFieldOption>();
    for (const item of node.slice(0, 5)) {
      for (const option of extractDocumentFields(item, parentField, parentSubPath, depth + 1)) {
        addHitFieldOption(fieldMap, option.field, option.path, option.type);
      }
    }
    return Array.from(fieldMap.values());
  }
  if (!isRecord(node)) {
    return [];
  }

  const fieldMap = new Map<string, TableResultFieldOption>();
  for (const [key, value] of Object.entries(node)) {
    const docField = parentField || key;
    const subPath = parentField ? [parentSubPath, key].filter(Boolean).join(".") : "";
    if (value == null) {
      addHitFieldOption(fieldMap, docField, subPath, "unknown");
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue;
      }
      if (parentField) {
        for (const option of extractDocumentFields(value, docField, subPath, depth + 1)) {
          addHitFieldOption(fieldMap, option.field, option.path, option.type);
        }
      } else {
        for (const option of extractDocumentFields(value, key, "", depth + 1)) {
          addHitFieldOption(fieldMap, option.field, option.path, option.type);
        }
      }
      continue;
    }
    if (isRecord(value)) {
      for (const option of extractDocumentFields(value, docField, subPath, depth + 1)) {
        addHitFieldOption(fieldMap, option.field, option.path, option.type);
      }
      continue;
    }
    addHitFieldOption(fieldMap, docField, subPath, metricType(value));
  }
  return Array.from(fieldMap.values());
}

function collectHitsSources(hits: unknown[]): TableResultSource[] {
  const sources: TableResultSource[] = [];
  const nestedFields = new Set<string>();

  for (const hit of hits.slice(0, 5)) {
    if (!isRecord(hit)) {
      continue;
    }
    const source = hit._source;
    if (!isRecord(source)) {
      continue;
    }
    for (const [key, value] of Object.entries(source)) {
      if (Array.isArray(value) && value.length > 0) {
        nestedFields.add(key);
      }
    }
  }

  if (hits.length > 0) {
    sources.push({
      id: "hits._root",
      kind: "hits",
      label: "hits (document root)",
      rowSource: "hits.",
    });
  }

  for (const nested of nestedFields) {
    sources.push({
      id: `hits.${nested}`,
      kind: "hits",
      label: `hits.${nested}`,
      rowSource: `hits.${nested}`,
    });
  }

  return sources;
}

function fieldsForHitsSource(source: TableResultSource, hits: unknown[]): TableResultFieldOption[] {
  if (hits.length === 0) {
    return [];
  }
  const rootSources = hits
    .slice(0, 10)
    .map((hit) => (isRecord(hit) ? hit._source : undefined))
    .filter(isRecord);

  const rowSource = source.rowSource || "";
  if (rowSource === "hits." || source.id === "hits._root") {
    const fieldMap = new Map<string, TableResultFieldOption>();
    for (const rootSource of rootSources) {
      for (const option of extractDocumentFields(rootSource)) {
        addHitFieldOption(fieldMap, option.field, option.path, option.type);
      }
    }
    return Array.from(fieldMap.values());
  }

  const nestedKey = rowSource.startsWith("hits.") ? rowSource.slice("hits.".length) : "";
  if (!nestedKey) {
    const fieldMap = new Map<string, TableResultFieldOption>();
    for (const rootSource of rootSources) {
      for (const option of extractDocumentFields(rootSource)) {
        addHitFieldOption(fieldMap, option.field, option.path, option.type);
      }
    }
    return Array.from(fieldMap.values());
  }

  const fieldMap = new Map<string, TableResultFieldOption>();
  for (const rootSource of rootSources) {
    const nested = rootSource[nestedKey];
    if (Array.isArray(nested) && nested.length > 0) {
      for (const item of nested.slice(0, 5)) {
        for (const option of extractDocumentFields(item)) {
          addHitFieldOption(fieldMap, option.field, option.path, option.type);
        }
      }
      continue;
    }
    if (isRecord(nested)) {
      for (const option of extractDocumentFields(nested)) {
        addHitFieldOption(fieldMap, option.field, option.path, option.type);
      }
    }
  }
  return Array.from(fieldMap.values());
}

export function analyzeOpenSearchTableResponse(response: unknown): OpenSearchTableAnalysis {
  const sources: TableResultSource[] = [];
  const fieldsBySource: Record<string, TableResultFieldOption[]> = {};

  if (!isRecord(response)) {
    return { sources, fieldsBySource };
  }

  const aggregations = isRecord(response.aggregations) ? response.aggregations : undefined;
  if (aggregations) {
    const siblingFields = extractSiblingAggregationFieldOptions(aggregations);
    if (siblingFields.length > 0) {
      sources.push({
        id: "aggregations",
        kind: "aggregation",
        label: "aggregations",
        aggPath: "aggregations",
      });
      fieldsBySource.aggregations = siblingFields;
    } else {
      const bucketMatch = findFirstBuckets(aggregations);
      if (bucketMatch) {
        const source: TableResultSource = {
          id: "aggregations",
          kind: "aggregation",
          label: bucketMatch.aggPath,
          aggPath: bucketMatch.aggPath,
        };
        sources.push(source);
        fieldsBySource[source.id] = extractAggBucketFields(
          bucketMatch.buckets,
          bucketMatch.aggPath,
          bucketMatch.aggNode
        );
      }
    }
  }

  const hitsContainer = isRecord(response.hits) ? response.hits.hits : undefined;
  const hits = Array.isArray(hitsContainer) ? hitsContainer : [];
  if (hits.length > 0) {
    const hitSources = collectHitsSources(hits);
    for (const source of hitSources) {
      sources.push(source);
      fieldsBySource[source.id] = fieldsForHitsSource(source, hits);
    }
  }

  return { sources, fieldsBySource };
}

export function resolveResultSourceId(
  analysis: OpenSearchTableAnalysis,
  rowSource?: string
): string | null {
  if (analysis.sources.length === 0) {
    return null;
  }
  if (!rowSource) {
    const agg = analysis.sources.find((source) => source.kind === "aggregation");
    return agg?.id ?? analysis.sources[0].id;
  }
  if (rowSource.startsWith("hits.")) {
    const exact = analysis.sources.find((source) => source.rowSource === rowSource);
    if (exact) {
      return exact.id;
    }
    if (rowSource === "hits.") {
      return analysis.sources.find((source) => source.id === "hits._root")?.id ?? null;
    }
  }
  const agg = analysis.sources.find((source) => source.kind === "aggregation");
  return agg?.id ?? analysis.sources[0].id;
}

export function resultSourceToRowSource(source: TableResultSource | undefined): string | undefined {
  if (!source) {
    return undefined;
  }
  if (source.kind === "aggregation") {
    return undefined;
  }
  return source.rowSource;
}
